/**
 * Leviathan - Tax Exports Page
 * Quasar Prime LLC
 *
 * Generate compliance-ready exports for tax filings
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  FileText,
  Download,
  Loader2,
  Calculator,
  Calendar,
  FileSpreadsheet,
  CheckCircle,
} from 'lucide-react';

import { API_URL } from '@/config/api';

interface TaxReturn {
  id: string;
  name: string;
  frequency: string;
  filingDeadline: string;
  filingMethod: string;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function TaxExports() {
  const { accessToken } = useAuth();
  const [selectedReturn, setSelectedReturn] = useState<string | null>(null);
  const [exportParams, setExportParams] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    quarter: Math.ceil((new Date().getMonth() + 1) / 3),
    startDate: '',
    endDate: '',
  });
  const [exportData, setExportData] = useState<any>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Fetch available tax returns
  const { data: taxReturnsData, isLoading } = useQuery({
    queryKey: ['tax-returns'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/tax-exports/returns`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch tax returns');
      return res.json();
    },
  });

  const taxReturns: TaxReturn[] = taxReturnsData?.returns || [];

  const generateExport = async () => {
    if (!selectedReturn) return;
    setIsExporting(true);
    setExportData(null);

    try {
      let url = '';
      const params = new URLSearchParams();

      switch (selectedReturn) {
        case 'tx-diesel-supplier':
        case 'tx-gasoline-distributor':
          params.append('month', exportParams.month.toString());
          params.append('year', exportParams.year.toString());
          url = `${API_URL}/api/tax-exports/tx-diesel-supplier?${params}`;
          break;
        case 'federal-720':
          params.append('quarter', exportParams.quarter.toString());
          params.append('year', exportParams.year.toString());
          url = `${API_URL}/api/tax-exports/form-720?${params}`;
          break;
        default:
          if (exportParams.startDate && exportParams.endDate) {
            params.append('startDate', exportParams.startDate);
            params.append('endDate', exportParams.endDate);
            url = `${API_URL}/api/tax-exports/sales?${params}`;
          }
      }

      if (url) {
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          setExportData(data.data || data);
        }
      }
    } catch (error) {
      console.error('Failed to generate export:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const downloadCSV = async (type: 'sales' | 'purchases') => {
    const params = new URLSearchParams();
    const startDateStr = new Date(exportParams.year, exportParams.month - 1, 1).toISOString().split('T')[0] || '';
    const endDateStr = new Date(exportParams.year, exportParams.month, 0).toISOString().split('T')[0] || '';
    const startDate = exportParams.startDate || startDateStr;
    const endDate = exportParams.endDate || endDateStr;
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('format', 'csv');

    const url = `${API_URL}/api/tax-exports/${type}?${params}`;

    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${type}_export_${startDate}_${endDate}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error('Failed to download CSV:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const selectedReturnData = taxReturns.find(r => r.id === selectedReturn);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Tax Exports</h1>
          <p className="text-slate-400 mt-1">
            Generate compliance-ready exports for tax filings
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left - Tax Returns List */}
        <div className="space-y-4">
          <Panel title="Available Returns">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
              </div>
            ) : (
              <div className="space-y-2">
                {taxReturns.map((taxReturn) => (
                  <button
                    key={taxReturn.id}
                    onClick={() => {
                      setSelectedReturn(taxReturn.id);
                      setExportData(null);
                    }}
                    className={`w-full text-left p-3 border rounded-lg transition-colors ${
                      selectedReturn === taxReturn.id
                        ? 'bg-amber-500/10 border-amber-500/50'
                        : 'bg-slate-900/50 border-slate-700 hover:border-amber-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className={`w-4 h-4 ${
                        selectedReturn === taxReturn.id ? 'text-amber-400' : 'text-slate-500'
                      }`} />
                      <span className={`text-sm font-medium ${
                        selectedReturn === taxReturn.id ? 'text-amber-400' : 'text-slate-100'
                      }`}>
                        {taxReturn.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-slate-500 text-xs">
                      <span>{taxReturn.frequency}</span>
                      <span>Due: {taxReturn.filingDeadline}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Panel>

          {/* Quick Export Buttons */}
          <Panel title="Quick Export">
            <div className="space-y-2">
              <button
                onClick={() => downloadCSV('sales')}
                className="w-full flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-slate-100 py-2 px-4 rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                Download Sales CSV
              </button>
              <button
                onClick={() => downloadCSV('purchases')}
                className="w-full flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-slate-100 py-2 px-4 rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                Download Purchases CSV
              </button>
            </div>
          </Panel>
        </div>

        {/* Middle - Parameters */}
        <div className="space-y-4">
          <Panel title="Export Parameters">
            {!selectedReturn ? (
              <div className="text-center py-8 text-slate-500">
                <Calendar className="w-8 h-8 mx-auto mb-2" />
                <p>Select a return type to configure</p>
              </div>
            ) : selectedReturn?.includes('720') ? (
              // Quarterly selection for Form 720
              <div className="space-y-4">
                <div>
                  <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">
                    Quarter
                  </label>
                  <select
                    value={exportParams.quarter}
                    onChange={(e) => setExportParams(prev => ({ ...prev, quarter: parseInt(e.target.value) }))}
                    className="w-full bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded-lg px-4 py-2 text-slate-100 focus:outline-none"
                  >
                    <option value={1}>Q1 (Jan - Mar)</option>
                    <option value={2}>Q2 (Apr - Jun)</option>
                    <option value={3}>Q3 (Jul - Sep)</option>
                    <option value={4}>Q4 (Oct - Dec)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">
                    Year
                  </label>
                  <select
                    value={exportParams.year}
                    onChange={(e) => setExportParams(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                    className="w-full bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded-lg px-4 py-2 text-slate-100 focus:outline-none"
                  >
                    {[2023, 2024, 2025].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            ) : (
              // Monthly selection for TX returns
              <div className="space-y-4">
                <div>
                  <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">
                    Month
                  </label>
                  <select
                    value={exportParams.month}
                    onChange={(e) => setExportParams(prev => ({ ...prev, month: parseInt(e.target.value) }))}
                    className="w-full bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded-lg px-4 py-2 text-slate-100 focus:outline-none"
                  >
                    {MONTHS.map((month, index) => (
                      <option key={index} value={index + 1}>{month}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">
                    Year
                  </label>
                  <select
                    value={exportParams.year}
                    onChange={(e) => setExportParams(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                    className="w-full bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded-lg px-4 py-2 text-slate-100 focus:outline-none"
                  >
                    {[2023, 2024, 2025].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {selectedReturn && (
              <>
                <div className="mt-4 p-3 bg-slate-900/50 border border-slate-700 rounded-lg">
                  <div className="text-slate-500 text-xs uppercase mb-1">Selected Return</div>
                  <div className="text-amber-400 font-medium">{selectedReturnData?.name}</div>
                  <div className="text-slate-400 text-sm mt-1">
                    {selectedReturnData?.filingMethod}
                  </div>
                </div>

                <button
                  onClick={generateExport}
                  disabled={isExporting}
                  className="w-full mt-4 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
                >
                  {isExporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Calculator className="w-4 h-4" />
                  )}
                  {isExporting ? 'Generating...' : 'Generate Export'}
                </button>
              </>
            )}
          </Panel>
        </div>

        {/* Right - Results */}
        <div>
          <Panel title="Export Results">
            {!exportData ? (
              <div className="text-center py-12 text-slate-500">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-4" />
                <p>Select a return and click Generate</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Summary */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Summary
                  </div>

                  {exportData.diesel && (
                    <div className="p-3 bg-slate-900/50 border border-slate-700 rounded-lg">
                      <div className="text-slate-100 font-medium mb-2">Diesel</div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-slate-500">Purchases:</div>
                        <div className="text-slate-100">{exportData.diesel.purchaseGallons.toLocaleString()} gal</div>
                        <div className="text-slate-500">Sales:</div>
                        <div className="text-slate-100">{exportData.diesel.salesGallons.toLocaleString()} gal</div>
                        <div className="text-slate-500">Tax Paid:</div>
                        <div className="text-slate-100">{formatCurrency(exportData.diesel.taxPaid)}</div>
                        <div className="text-slate-500">Tax Collected:</div>
                        <div className="text-slate-100">{formatCurrency(exportData.diesel.taxCollected)}</div>
                      </div>
                    </div>
                  )}

                  {exportData.gasoline && (
                    <div className="p-3 bg-slate-900/50 border border-slate-700 rounded-lg">
                      <div className="text-slate-100 font-medium mb-2">Gasoline</div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-slate-500">Purchases:</div>
                        <div className="text-slate-100">{exportData.gasoline.purchaseGallons.toLocaleString()} gal</div>
                        <div className="text-slate-500">Sales:</div>
                        <div className="text-slate-100">{exportData.gasoline.salesGallons.toLocaleString()} gal</div>
                        <div className="text-slate-500">Tax Paid:</div>
                        <div className="text-slate-100">{formatCurrency(exportData.gasoline.taxPaid)}</div>
                        <div className="text-slate-500">Tax Collected:</div>
                        <div className="text-slate-100">{formatCurrency(exportData.gasoline.taxCollected)}</div>
                      </div>
                    </div>
                  )}

                  {exportData.purchaseGallons !== undefined && (
                    <div className="p-3 bg-slate-900/50 border border-slate-700 rounded-lg">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-slate-500">Purchase Gallons:</div>
                        <div className="text-slate-100">{exportData.purchaseGallons.toLocaleString()}</div>
                        <div className="text-slate-500">Sales Gallons:</div>
                        <div className="text-slate-100">{exportData.salesGallons.toLocaleString()}</div>
                        <div className="text-slate-500">Tax Paid:</div>
                        <div className="text-slate-100">{formatCurrency(exportData.taxPaid)}</div>
                        <div className="text-slate-500">Tax Collected:</div>
                        <div className="text-slate-100">{formatCurrency(exportData.taxCollected)}</div>
                        <div className="text-amber-400 font-semibold">Net Tax Due:</div>
                        <div className="text-amber-400 font-semibold">{formatCurrency(exportData.netTaxDue)}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Record Counts */}
                <div className="space-y-2">
                  <div className="text-slate-400 text-xs uppercase tracking-wider">Records</div>
                  <div className="text-sm">
                    <span className="text-slate-500">Purchase Records: </span>
                    <span className="text-slate-100">{exportData.purchaseRecords?.length || 0}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-slate-500">Sales Records: </span>
                    <span className="text-slate-100">{exportData.salesRecords?.length || 0}</span>
                  </div>
                </div>

                {/* Download Buttons */}
                <div className="space-y-2 pt-4 border-t border-slate-700">
                  <button
                    onClick={() => downloadCSV('purchases')}
                    className="w-full flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-slate-100 py-2 px-4 rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download Purchase Data (CSV)
                  </button>
                  <button
                    onClick={() => downloadCSV('sales')}
                    className="w-full flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-slate-100 py-2 px-4 rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download Sales Data (CSV)
                  </button>
                </div>
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber-500/50 rounded-tl-xl" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber-500/50 rounded-tr-xl" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber-500/50 rounded-bl-xl" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber-500/50 rounded-br-xl" />

      <div className="p-5">
        <h3 className="text-lg font-semibold text-slate-100 uppercase tracking-wide mb-4 pb-3 border-b border-amber-500/30">
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}
